/**
 * useJewelInteraction Hook
 *
 * Manages jewel selection, swapping, and interaction logic for mfersbeheaded game.
 * Extracted from Board.js as part of refactoring effort.
 *
 * This hook handles:
 * - Jewel selection state
 * - Swap validation
 * - Power-up activation
 * - Game state transitions on first click
 */

import React, { useState, useEffect, useCallback } from 'react';
import { isSuperPowerUp, isDeadPiece } from '../MferHead';
import { applyGravity, applyGravityEnhanced, fillWithNewJewels } from '../gameLogic';
import CONSTANTS from '../constants';
import logger from '../../../utils/logger';
import { GameBoard, StateChange } from '../../../types/games/mfersbeheaded';

interface JewelPosition {
  rowIndex: number;
  colIndex: number;
}

interface BoardStateHook {
  hasHorizontalMatchAt: (board: GameBoard, row: number, col: number) => boolean;
  hasVerticalMatchAt: (board: GameBoard, row: number, col: number) => boolean;
  sweepBoard: (
    board: GameBoard,
    playSound: () => void,
    playAdvancedSoundNTimes: (n: number) => void,
    livePlay: boolean,
    elapsedTimeSince: () => number
  ) => Promise<[number, Array<{ t: number; changes: StateChange[] }>]>;
  applySuperPowerUpWithUpdates: (board: GameBoard, rowIndex: number, colIndex: number) => Promise<[number, StateChange[]]>;
}

interface GameStateHook {
  elapsedTimeSince: () => number;
  isGameInProgress: () => boolean;
  isGameComplete: () => boolean;
  isGameNotYetBegun: () => boolean;
  startGame: () => void;
  endGame: () => void;
  updateScoreAndMoves: (points: number, reason?: string) => { newScore: number; newMoves: number };
  updateScoreForPowerUp: (points: number) => number;
  setGameState: React.Dispatch<React.SetStateAction<number>>;
  gameStats: {
    totalScore: number;
    powerUps: number;
    movesUsed: number;
  };
}

interface GravityMovement {
  from: [number, number];
  to: [number, number];
  value: number;
}

interface ReplayHook {
  recordSwap: (t: number, from: [number, number], to: [number, number], valid: boolean, fromValue?: number | null, toValue?: number | null) => void;
  recordPowerUp: (t: number, position: [number, number], cleared: number) => void;
  recordBoardState: (t: number, changes: StateChange[], label: string) => void;
  recordBoardStateList: (changes: Array<{ t: number; changes: StateChange[] }>, label: string) => void;
  recordScoreUpdate: (t: number, score: number, points: number, reason: string) => void;
  recordGameState: (t: number, state: number, moves: number, elapsed: number) => void;
  updateSession: (data: Record<string, any>) => void;
  isRecorderReady: () => boolean;
  // NEW: Enhanced event recording
  recordClear: (t: number, clearedCells: StateChange[], reason?: string) => void;
  recordGravity: (t: number, movements: GravityMovement[]) => void;
  recordFill: (t: number, newPieces: StateChange[]) => void;
}

interface AudioHook {
  playSound: () => void;
  playWrongSound: () => void;
  playSuperPowerUpSound: () => void;
}

interface UseJewelInteractionParams {
  board: GameBoard;
  gameState: number;
  BOARD_SIZE: number;
  BARRELS_ENABLED: boolean;
  TIME_LIMIT: number | null;
  MAX_MOVES: number | null;

  // Hooks/utilities
  boardStateHook: BoardStateHook;
  gameStateHook: GameStateHook;
  replayHook: ReplayHook;
  audioHook: AudioHook;

  // Functions
  playAdvancedSoundNTimes: (n: number) => void;
}

interface UseJewelInteractionReturn {
  selectedJewel: JewelPosition | null;
  setSelectedJewel: React.Dispatch<React.SetStateAction<JewelPosition | null>>;
  handleJewelClicked: (rowIndex: number, colIndex: number) => void;
}

export const useJewelInteraction = ({
  board,
  gameState,
  BOARD_SIZE,
  BARRELS_ENABLED,
  TIME_LIMIT,
  MAX_MOVES,

  // Hooks/utilities
  boardStateHook,
  gameStateHook,
  replayHook,
  audioHook,

  // Functions
  playAdvancedSoundNTimes,
}: UseJewelInteractionParams): UseJewelInteractionReturn => {
  const [selectedJewel, setSelectedJewel] = useState<JewelPosition | null>(null);
  const [clickedJewel, setClickedJewel] = useState<JewelPosition | null>(null);

  const {
    hasHorizontalMatchAt,
    hasVerticalMatchAt,
    sweepBoard,
    applySuperPowerUpWithUpdates,
  } = boardStateHook;

  const {
    elapsedTimeSince,
    isGameInProgress,
    isGameComplete,
    isGameNotYetBegun,
    startGame,
    endGame,
    updateScoreAndMoves,
    updateScoreForPowerUp,
    setGameState,
    gameStats,
  } = gameStateHook;

  const {
    recordSwap,
    recordPowerUp,
    recordBoardState,
    recordBoardStateList,
    recordScoreUpdate,
    recordGameState,
    updateSession,
    isRecorderReady,
    recordClear,
    recordGravity,
    recordFill,
  } = replayHook;

  const {
    playSound,
    playWrongSound,
    playSuperPowerUpSound,
  } = audioHook;

  /**
   * Handle jewel click - the main interaction logic
   */
  useEffect(() => {
    async function handleJewelClick() {
      if (!clickedJewel) return;

      const { rowIndex, colIndex } = clickedJewel;

      // Don't allow clicks when game is complete
      if (isGameComplete()) {
        return;
      }

      // Start the game on first click
      if (isGameNotYetBegun()) {
        startGame();

        // Record game start
        if (isRecorderReady()) {
          recordGameState(
            elapsedTimeSince(),
            CONSTANTS.GAME_STATE_IN_PROGRESS,
            0,
            elapsedTimeSince()
          );

          updateSession({
            game_state: CONSTANTS.GAME_STATE_IN_PROGRESS
          });
        }
      }

      // Handle power-up selection/deselection
      if (selectedJewel && isSuperPowerUp(board[rowIndex][colIndex])) {
        setSelectedJewel(null);
        return;
      }

      // Handle power-up activation
      if (!selectedJewel && isSuperPowerUp(board[rowIndex][colIndex])) {
        setSelectedJewel({ rowIndex, colIndex });
        playSuperPowerUpSound();

        const [nClearedFromSuperPowerUp, stateChangesFromSuperPowerUp] =
          await applySuperPowerUpWithUpdates(board, rowIndex, colIndex);

        // Record power-up activation
        if (isRecorderReady()) {
          recordPowerUp(
            elapsedTimeSince(),
            [rowIndex, colIndex],
            nClearedFromSuperPowerUp
          );
          recordBoardState(
            elapsedTimeSince(),
            stateChangesFromSuperPowerUp,
            'power_up'
          );
        }

        // Update score for power-up (only if game is active)
        if (!isGameComplete() && !isGameNotYetBegun()) {
          const newScore = updateScoreForPowerUp(nClearedFromSuperPowerUp);

          // Record score update
          if (isRecorderReady()) {
            recordScoreUpdate(
              elapsedTimeSince(),
              newScore,
              nClearedFromSuperPowerUp,
              'power_up'
            );
          }
        }

        // Animation and cascading effects
        for (let i = 0; i < 4; i++) {
          playSound();
          await new Promise(resolve => setTimeout(resolve, CONSTANTS.TIMING.ANIMATION_DELAY));
        }

        // Apply gravity with enhanced recording
        const gravityResult = applyGravityEnhanced(board, BOARD_SIZE);
        logger.debug('Gravity movements', { count: gravityResult.movements.length }, 'replay');

        if (isRecorderReady()) {
          // Record gravity movements (enhanced)
          if (gravityResult.movements.length > 0) {
            recordGravity(elapsedTimeSince(), gravityResult.movements);
          }
          // Still record board state for backwards compatibility
          if (gravityResult.stateChanges.length > 0) {
            recordBoardState(
              elapsedTimeSince(),
              gravityResult.stateChanges,
              'gravity'
            );
          }
        }

        // Fill with new jewels
        const [, stateChangesFromJewels] = fillWithNewJewels(board, BOARD_SIZE, CONSTANTS.N_MFERHEADS);

        if (isRecorderReady() && stateChangesFromJewels.length > 0) {
          // Record fill events (enhanced)
          recordFill(elapsedTimeSince(), stateChangesFromJewels);
          // Still record board state for backwards compatibility
          recordBoardState(
            elapsedTimeSince(),
            stateChangesFromJewels,
            'fill'
          );
        }

        // Sweep board for cascading matches
        let [nClearedThisRound, stateChangesFromSweepBoard] = await sweepBoard(
          board,
          playSound,
          playAdvancedSoundNTimes,
          true,
          elapsedTimeSince
        );

        logger.debug('Sweep board state changes', { count: stateChangesFromSweepBoard.length }, 'replay');

        if (isRecorderReady() && stateChangesFromSweepBoard.length > 0) {
          logger.debug('Recording sweep board changes to replay', { count: stateChangesFromSweepBoard.length }, 'replay');

          // Record clear/gravity/fill events for each sweep batch (enhanced)
          // Batch pattern: [clear, gravity, fill, clear (cascade), gravity, fill, clear, ...]
          stateChangesFromSweepBoard.forEach((batch, batchIndex) => {
            // Determine batch type based on position
            let batchType: 'clear' | 'gravity' | 'fill';
            if (batchIndex === 0) {
              batchType = 'clear';
            } else if (batchIndex % 3 === 1) {
              batchType = 'gravity';
            } else if (batchIndex % 3 === 2) {
              batchType = 'fill';
            } else {
              batchType = 'clear'; // cascade clear
            }

            if (batchType === 'clear') {
              // Extract cleared cells (cells that were marked as dead)
              const clearedCells = batch.changes.filter(([_r, _c, value]) => isDeadPiece(value));

              if (clearedCells.length > 0) {
                recordClear(batch.t, clearedCells, 'cascade');
              }
            } else if (batchType === 'gravity') {
              // Gravity batch should have movements attached (from applyGravityEnhanced)
              const movements = (batch as any).movements || [];

              if (movements.length > 0) {
                recordGravity(batch.t, movements);
              }
            } else if (batchType === 'fill') {
              // Fill changes are new pieces
              const newPieces = batch.changes;

              if (newPieces.length > 0) {
                recordFill(batch.t, newPieces);
              }
            }
          });

          // Note: Deprecated BOARD_STATE events removed - we now use semantic CLEAR/GRAVITY/FILL events
        }

        // Update score from cascading matches (only if game is active)
        if (!isGameComplete() && !isGameNotYetBegun()) {
          const newScore = updateScoreForPowerUp(nClearedThisRound);

          // Record score update for cascading matches
          if (isRecorderReady() && nClearedThisRound > 0) {
            recordScoreUpdate(
              elapsedTimeSince(),
              newScore,
              nClearedThisRound,
              'cascade'
            );
          }
        }

        setSelectedJewel(null);
        return;
      }

      // Handle regular jewel selection
      if (!selectedJewel) {
        setSelectedJewel({ rowIndex, colIndex });
        return;
      }

      // Check if adjacent
      const isAdjacent =
        Math.abs(selectedJewel.rowIndex - rowIndex) +
        Math.abs(selectedJewel.colIndex - colIndex) === 1;

      if (isAdjacent) {
        // Capture cell values before swap for enhanced recording
        const fromValue = board[selectedJewel.rowIndex][selectedJewel.colIndex];
        const toValue = board[rowIndex][colIndex];

        // Perform swap
        const newBoard = [...board];
        const temp = newBoard[rowIndex][colIndex];
        newBoard[rowIndex][colIndex] = newBoard[selectedJewel.rowIndex][selectedJewel.colIndex];
        newBoard[selectedJewel.rowIndex][selectedJewel.colIndex] = temp;

        // Validate swap
        let isValidSwap =
          hasHorizontalMatchAt(newBoard, rowIndex, colIndex) ||
          hasVerticalMatchAt(newBoard, rowIndex, colIndex) ||
          hasHorizontalMatchAt(newBoard, selectedJewel.rowIndex, selectedJewel.colIndex) ||
          hasVerticalMatchAt(newBoard, selectedJewel.rowIndex, selectedJewel.colIndex);

        if (isValidSwap) {
          // Record swap event with cell values (enhanced)
          if (isRecorderReady()) {
            recordSwap(
              elapsedTimeSince(),
              [selectedJewel.rowIndex, selectedJewel.colIndex],
              [rowIndex, colIndex],
              true,
              fromValue,  // NEW
              toValue     // NEW
            );
          }

          setSelectedJewel(null);

          // Sweep board and get score
          let [nScored, stateChangesFromSweep] = await sweepBoard(
            board,
            playSound,
            playAdvancedSoundNTimes,
            true,
            elapsedTimeSince
          );

          logger.debug('Swap sweep board state changes', { count: stateChangesFromSweep.length }, 'replay');

          // Record sweep changes
          if (isRecorderReady() && stateChangesFromSweep.length > 0) {
            logger.debug('Recording swap sweep board changes to replay', { count: stateChangesFromSweep.length }, 'replay');

            // Record clear/gravity/fill events for each sweep batch (enhanced)
            // Batch pattern: [clear, gravity, fill, clear (cascade), gravity, fill, clear, ...]
            // Batch 0 = initial clear
            // Then repeating: gravity (i%3==1), fill (i%3==2), clear (i%3==0 && i>0)
            stateChangesFromSweep.forEach((batch, batchIndex) => {
              // Determine batch type based on position
              let batchType: 'clear' | 'gravity' | 'fill';
              if (batchIndex === 0) {
                batchType = 'clear';
              } else if (batchIndex % 3 === 1) {
                batchType = 'gravity';
              } else if (batchIndex % 3 === 2) {
                batchType = 'fill';
              } else {
                batchType = 'clear'; // cascade clear
              }

              if (batchType === 'clear') {
                // Extract cleared cells (cells that were marked as dead)
                const clearedCells = batch.changes.filter(([_r, _c, value]) => isDeadPiece(value));

                if (clearedCells.length > 0) {
                  recordClear(batch.t, clearedCells, batchIndex === 0 ? 'match' : 'cascade');
                }
              } else if (batchType === 'gravity') {
                // Gravity batch should have movements attached (from applyGravityEnhanced)
                const movements = (batch as any).movements || [];

                if (movements.length > 0) {
                  recordGravity(batch.t, movements);
                }
              } else if (batchType === 'fill') {
                // Fill changes are new pieces
                const newPieces = batch.changes;

                if (newPieces.length > 0) {
                  recordFill(batch.t, newPieces);
                }
              }
            });

            // Note: Deprecated BOARD_STATE events removed - we now use semantic CLEAR/GRAVITY/FILL events
          }

          // Update score and moves (only if game is active)
          if (!isGameComplete() && !isGameNotYetBegun()) {
            const { newScore, newMoves } = updateScoreAndMoves(nScored, 'match');

            // Record score update
            if (isRecorderReady()) {
              recordScoreUpdate(
                elapsedTimeSince(),
                newScore,
                nScored,
                'match'
              );

              // Record game state update with new moves count
              recordGameState(
                elapsedTimeSince(),
                gameState,
                newMoves,
                elapsedTimeSince()
              );

              // Check if player has exceeded max moves
              if (MAX_MOVES !== null && newMoves >= MAX_MOVES) {
                recordGameState(
                  elapsedTimeSince(),
                  CONSTANTS.GAME_STATE_COMPLETE,
                  newMoves,
                  elapsedTimeSince()
                );
              }
            }
          }
        } else {
          // Invalid swap - record and play sound (with cell values)
          if (isRecorderReady()) {
            recordSwap(
              elapsedTimeSince(),
              [selectedJewel.rowIndex, selectedJewel.colIndex],
              [rowIndex, colIndex],
              false,
              fromValue,  // NEW
              toValue     // NEW
            );
          }

          playWrongSound();

          // Swap back
          const temp = newBoard[rowIndex][colIndex];
          newBoard[rowIndex][colIndex] = newBoard[selectedJewel.rowIndex][selectedJewel.colIndex];
          newBoard[selectedJewel.rowIndex][selectedJewel.colIndex] = temp;
        }
      } else {
        // Not adjacent - play wrong sound
        playWrongSound();
      }

      setSelectedJewel(null);
    }

    handleJewelClick();
  }, [clickedJewel]);

  /**
   * Trigger a jewel click
   * @param {number} rowIndex - Row index
   * @param {number} colIndex - Column index
   */
  const handleJewelClicked = useCallback((rowIndex: number, colIndex: number): void => {
    setClickedJewel({ rowIndex, colIndex });
  }, []);

  return {
    selectedJewel,
    setSelectedJewel,
    handleJewelClicked,
  };
};

export default useJewelInteraction;
